import React, { useState, useEffect, useRef } from "react";
import { safeGetItem, safeSetItem } from "../../utils/safeStorage";
import "./DesktopInstallBanner.css";

const DISMISS_DAYS = 7;
const DISMISS_KEY = "install_banner_dismissed";

let _deferredPrompt = null;

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferredPrompt = e;
  });
}

const DesktopInstallBanner = ({ onClose }) => {
  const [bannerType, setBannerType] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);
  const deferredPromptRef = useRef(null);

  useEffect(() => {
    const dismissed = safeGetItem(DISMISS_KEY);
    if (dismissed) {
      const expiry = parseInt(dismissed, 10);
      if (!isNaN(expiry) && Date.now() < expiry) return;
    }

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );
    if (isMobile) return;

    if (window.matchMedia("(display-mode: standalone)").matches) return;

    if (_deferredPrompt) {
      deferredPromptRef.current = _deferredPrompt;
      setBannerType("chrome");
      return;
    }

    let promptReceived = false;
    let cancelled = false;

    const handleBeforeInstall = (e) => {
      e.preventDefault();
      promptReceived = true;
      _deferredPrompt = e;
      deferredPromptRef.current = e;
      if (!cancelled) setBannerType("chrome");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstall);

    const timer = setTimeout(() => {
      if (cancelled) return;
      if (!promptReceived) {
        const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
        if (isSafari) setBannerType("safari");
      }
    }, 300);

    return () => {
      cancelled = true;
      window.removeEventListener("beforeinstallprompt", handleBeforeInstall);
      clearTimeout(timer);
    };
  }, []);

  const handleInstall = async () => {
    const prompt = deferredPromptRef.current;
    if (!prompt || isInstalling) return;

    setIsInstalling(true);

    try {
      await prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") {
        deferredPromptRef.current = null;
        setBannerType(null);
        if (onClose) onClose();
      }
    } catch (error) {
      console.error("Install error:", error);
    } finally {
      setIsInstalling(false);
    }
  };

  const handleClose = () => {
    const expiry = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    safeSetItem(DISMISS_KEY, expiry.toString());
    setBannerType(null);
    if (onClose) onClose();
  };

  if (!bannerType) return null;

  if (bannerType === "chrome") {
    return (
      <div className="desktop-install-banner">
        <button
          className="close-banner-btn"
          onClick={handleClose}
          title="بستن"
          aria-label="بستن بنر نصب"
        >
          <i className="fas fa-times"></i>
        </button>
        <div className="banner-content">
          <i className="fas fa-desktop banner-icon"></i>
          <p className="banner-text">
            برای تجربه بهتر برنامه اوملنگ را روی کامپیوتر خود نصب کنید
          </p>
        </div>
        <button className="install-banner-btn" onClick={handleInstall} disabled={isInstalling}>
          <i className={`fas ${isInstalling ? "fa-spinner fa-spin" : "fa-download"}`}></i>
          {isInstalling ? "در حال نصب..." : "نصب برنامه"}
        </button>
      </div>
    );
  }

  if (bannerType === "safari") {
    return (
      <div className="desktop-install-banner safari-banner">
        <button
          className="close-banner-btn"
          onClick={handleClose}
          title="بستن"
          aria-label="بستن بنر نصب"
        >
          <i className="fas fa-times"></i>
        </button>
        <div className="banner-content">
          <i className="fas fa-compass banner-icon"></i>
          <p className="banner-text">
            برای نصب برنامه از منوی File → Add to Dock استفاده کنید
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default DesktopInstallBanner;
